/**
 * 🚀 Paraglide JS Locale Management Hook
 * 
 * Zero Runtime, Complete Type Safety!
 * Integrates with shared i18n-lang localStorage key
 */ function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { useState, useEffect } from 'react';
import { getLocale, setLocale, locales, baseLocale } from '@paraglide/runtime';
import { I18N_LANG_KEY } from '@edusense/shared-constants';
export var useParaglideLocale = function() {
    // 🚀 localStorage 우선 초기값 설정 - 동기화 문제 해결
    var _useState = _sliced_to_array(useState(function() {
        if (typeof window !== 'undefined') {
            try {
                // 1. localStorage에서 먼저 확인 (AuthServerApp에서 설정한 값)
                var storedLang = localStorage.getItem(I18N_LANG_KEY);
                if (storedLang && locales.includes(storedLang)) {
                    console.log("\uD83D\uDE80 [useParaglideLocale] Initial locale from localStorage: ".concat(storedLang));
                    // Paraglide 전역 상태도 localStorage 값으로 즉시 동기화
                    try {
                        setLocale(storedLang, {
                            reload: false
                        });
                        console.log("\uD83D\uDD04 [useParaglideLocale] Synced Paraglide to localStorage value: ".concat(storedLang));
                    } catch (error) {
                        console.warn("⚠️ [useParaglideLocale] Failed to sync Paraglide:", error);
                    }
                    return storedLang;
                }
                // 2. Paraglide에서 현재 locale 확인
                var currentParaglideLocale = getLocale();
                console.log("\uD83D\uDE80 [useParaglideLocale] Initial locale from Paraglide: ".concat(currentParaglideLocale));
                return currentParaglideLocale;
            } catch (error) {
                console.warn("⚠️ [useParaglideLocale] Initialization failed, using fallback:", error);
                return 'ko'; // 기본값
            }
        }
        return 'ko';
    }), 2), currentLocale = _useState[0], setCurrentLocale = _useState[1];
    // Sync with existing i18n-lang localStorage for compatibility + Paraglide runtime sync
    useEffect(function() {
        // 1. localStorage 동기화 체크
        var storedLang = localStorage.getItem(I18N_LANG_KEY);
        // 2. Paraglide 런타임 상태 체크
        var currentParaglideLocale = getLocale();
        console.log("\uD83D\uDD0D [useParaglideLocale] Sync check:", {
            storedLang: storedLang,
            currentLocale: currentLocale,
            currentParaglideLocale: currentParaglideLocale
        });
        // 3. Paraglide 런타임과 React state가 다르면 React state 업데이트
        if (currentParaglideLocale && currentParaglideLocale !== currentLocale) {
            console.log("\uD83D\uDD04 [useParaglideLocale] Syncing to Paraglide runtime: ".concat(currentLocale, " → ").concat(currentParaglideLocale));
            setCurrentLocale(currentParaglideLocale);
            return; // Paraglide 우선으로 동기화하고 리턴
        }
        // 4. localStorage와 현재 locale이 다르면 localStorage 우선 적용
        if (storedLang && storedLang !== currentLocale) {
            if (locales.includes(storedLang)) {
                console.log("\uD83D\uDD04 [useParaglideLocale] Updating from localStorage: ".concat(currentLocale, " → ").concat(storedLang));
                setLocale(storedLang, {
                    reload: false
                });
                setCurrentLocale(storedLang);
            } else {
                console.warn("⚠️ [useParaglideLocale] Invalid stored language: ".concat(storedLang, ", available: ").concat(locales));
            }
        } else if (storedLang === currentLocale && currentParaglideLocale === currentLocale) {
            console.log("✅ [useParaglideLocale] All in sync: ".concat(currentLocale));
        } else if (!storedLang) {
            console.log("\uD83D\uDCDD [useParaglideLocale] No stored language, using default: ".concat(currentLocale));
        }
    }, [
        currentLocale
    ]);
    // 5. 🚀 글로벌 언어 동기화 이벤트 리스너 (useLanguageSync와 연동)
    useEffect(function() {
        var handleLanguageSyncComplete = function(event) {
            var _event_detail = event.detail, newLocale = _event_detail.newLocale, source = _event_detail.source;
            console.log("\uD83D\uDE80 [useParaglideLocale] Received language-sync-complete:", {
                newLocale: newLocale,
                source: source,
                currentLocale: currentLocale,
                willUpdate: newLocale !== currentLocale
            });
            if (newLocale && newLocale !== currentLocale) {
                console.log("\uD83D\uDD04 [useParaglideLocale] Syncing to event: ".concat(currentLocale, " → ").concat(newLocale));
                setCurrentLocale(newLocale);
            }
        };
        // 새로운 통합 이벤트 리스너 등록
        window.addEventListener('language-sync-complete', handleLanguageSyncComplete);
        // 레거시 호환성을 위한 기존 이벤트도 유지
        window.addEventListener('paraglide-locale-changed', handleLanguageSyncComplete);
        return function() {
            window.removeEventListener('language-sync-complete', handleLanguageSyncComplete);
            window.removeEventListener('paraglide-locale-changed', handleLanguageSyncComplete);
        };
    }, [
        currentLocale
    ]);
    var changeLocale = function(newLocale) {
        if (locales.includes(newLocale)) {
            // Update Paraglide JS locale
            setLocale(newLocale, {
                reload: false
            });
            // Update localStorage using shared constant
            localStorage.setItem(I18N_LANG_KEY, newLocale);
            // Update local state
            setCurrentLocale(newLocale);
        }
    };
    return {
        currentLocale: currentLocale,
        changeLocale: changeLocale,
        availableLocales: locales,
        baseLocale: baseLocale
    };
};
